import type { JSX, ReactNode } from 'react';
import { SHIPMENTS_WEIGH_EVIDENCE_CAPTURE_PERMISSION } from '@crv/contracts/permissions';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BadgeIcon from '@mui/icons-material/Badge';
import BusinessIcon from '@mui/icons-material/Business';
import CategoryIcon from '@mui/icons-material/Category';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DownloadIcon from '@mui/icons-material/Download';
import GroupsIcon from '@mui/icons-material/Groups';
import HomeIcon from '@mui/icons-material/Home';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import MailIcon from '@mui/icons-material/Mail';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ScaleIcon from '@mui/icons-material/Scale';
import SecurityIcon from '@mui/icons-material/Security';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { NavLink } from 'react-router-dom';

import { getAssignedShipmentQuickCaptureAvailability } from '../api/shipments/shipments.api';
import { shipmentsKeys } from '../api/shipments/shipments.hooks.shared';
import { useAuth } from '../auth/AuthProvider';
import { hasFeature, hasPermission } from '../auth/authorization';
import {
  canAccessQuickCaptureNavigation,
  canAccessWebModule,
  type ModuleKey,
} from '../auth/moduleAccessPolicy';
import {
  getTrialDaysRemaining,
  isPlanUsable,
  isTrial,
  isTrialExpired,
} from '../billing/planStatus';
import { formatPlanBillingLabel } from '../billing/planBillingLabels';
import { useTenancyStore } from '../tenancy/tenancy-store';
import { AppShell, type ShellNavGroup, type ShellNavItem } from './AppShell';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';

interface ModuleNavItem extends ShellNavItem {
  module?: ModuleKey;
  icon?: ReactNode;
}

interface ModuleNavGroup extends Omit<ShellNavGroup, 'items'> {
  items: readonly ModuleNavItem[];
}

const navIconProps = { fontSize: 'small' } as const;

const COMPANY_NAV_GROUPS: readonly ModuleNavGroup[] = [
  {
    id: 'workspace',
    label: 'Workspace',
    items: [{ to: '/', label: 'Home', icon: <HomeIcon {...navIconProps} />, end: true }],
  },
  {
    id: 'operations',
    label: 'Operations',
    items: [
      {
        to: '/shipments',
        label: 'Shipments',
        module: 'shipments',
        icon: <LocalShippingIcon {...navIconProps} />,
        activeWhen: ['/shipments'],
        inactiveWhen: ['/shipments/quick-capture'],
      },
      {
        to: '/shipments/quick-capture',
        label: 'Quick Capture',
        module: 'shipmentQuickCapture',
        icon: <ScaleIcon {...navIconProps} />,
      },
      {
        to: '/holding-units',
        label: 'Holding Units',
        module: 'holdingUnits',
        icon: <Inventory2Icon {...navIconProps} />,
      },
      { to: '/cash', label: 'Cash', module: 'cash', icon: <PointOfSaleIcon {...navIconProps} /> },
    ],
  },
  {
    id: 'relationships',
    label: 'Customers & Workforce',
    items: [
      {
        to: '/customers',
        label: 'Customers',
        module: 'customers',
        icon: <BusinessIcon {...navIconProps} />,
      },
      {
        to: '/employees',
        label: 'Employees',
        module: 'employees',
        icon: <BadgeIcon {...navIconProps} />,
      },
      {
        to: '/time-tracking',
        label: 'Time tracking',
        module: 'timeTracking',
        icon: <AccessTimeIcon {...navIconProps} />,
      },
      {
        to: '/time-tracking/self',
        label: 'Self clock',
        module: 'timeTrackingSelf',
        icon: <DashboardIcon {...navIconProps} />,
      },
    ],
  },
  {
    id: 'administration',
    label: 'Administration',
    items: [
      {
        to: '/admin/company',
        label: 'Company',
        module: 'company',
        icon: <BusinessIcon {...navIconProps} />,
      },
      {
        to: '/admin/stores',
        label: 'Stores',
        module: 'stores',
        icon: <StorefrontIcon {...navIconProps} />,
        activeWhen: ['/admin/stores', '/admin/locations'],
      },
      { to: '/admin/team', label: 'Team', module: 'team', icon: <GroupsIcon {...navIconProps} /> },
      {
        to: '/admin/invites',
        label: 'Invites',
        module: 'invites',
        icon: <MailIcon {...navIconProps} />,
      },
      {
        to: '/admin/roles',
        label: 'Roles',
        module: 'companyRoles',
        icon: <SecurityIcon {...navIconProps} />,
      },
      {
        to: '/admin/materials-catalog',
        label: 'Materials',
        module: 'materialsCatalog',
        icon: <CategoryIcon {...navIconProps} />,
        activeWhen: ['/admin/materials-catalog', '/admin/materials-presentation'],
      },
      {
        to: '/admin/company-receivers',
        label: 'Receivers',
        module: 'companyReceivers',
        icon: <LocalShippingIcon {...navIconProps} />,
      },
      {
        to: '/admin/offline/export',
        label: 'Offline',
        module: 'offline',
        icon: <DownloadIcon {...navIconProps} />,
      },
    ],
  },
  {
    id: 'account',
    label: 'Account',
    items: [{ to: '/billing', label: 'Billing', icon: <ReceiptLongIcon {...navIconProps} /> }],
  },
];

export function MainLayout(): JSX.Element {
  const auth = useAuth();
  const currentUser = auth.currentUser;
  const bootstrap = auth.bootstrap;
  const activeCompany = currentUser?.activeCompany?.company ?? null;
  const selectedCompanyId = useTenancyStore((state) => state.selectedCompanyId);
  const activeCompanyId = selectedCompanyId ?? activeCompany?.id ?? null;
  const activeCompanyName = activeCompany?.name;
  const planUsable = isPlanUsable(activeCompany);
  const trialExpired = isTrialExpired(activeCompany);
  const trialDaysRemaining = getTrialDaysRemaining(activeCompany);
  const hasShipmentsFeature = hasFeature(currentUser, 'shipments.basic');
  const hasBroadQuickCapturePermission = hasPermission(
    currentUser,
    SHIPMENTS_WEIGH_EVIDENCE_CAPTURE_PERMISSION,
  );
  const assignedQuickCaptureAvailabilityQuery = useQuery({
    queryKey: shipmentsKeys.quickCaptureAssignedAvailability({
      companyId: activeCompanyId ?? '',
    }),
    queryFn: () =>
      getAssignedShipmentQuickCaptureAvailability({
        companyId: activeCompanyId ?? '',
      }),
    enabled: Boolean(activeCompanyId && hasShipmentsFeature && !hasBroadQuickCapturePermission),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
  const hasActiveAssignedCaptureTargets =
    assignedQuickCaptureAvailabilityQuery.data?.hasActiveAssignedCaptureTargets === true;
  const visibleNavGroups: ShellNavGroup[] = COMPANY_NAV_GROUPS.map((group) => ({
    id: group.id,
    label: group.label,
    items: group.items.filter((item) => {
      if (!item.module) {
        return true;
      }
      if (item.module === 'shipmentQuickCapture') {
        return canAccessQuickCaptureNavigation(currentUser, hasActiveAssignedCaptureTargets);
      }
      return canAccessWebModule(currentUser, item.module);
    }),
  })).filter((group) => group.items.length > 0);

  const bannerMessage = activeCompany
    ? ((): string => {
        if (!planUsable) {
          return isTrial(activeCompany) && trialExpired
            ? 'Trial expired. Plan is inactive until billing is set up.'
            : 'Plan inactive. Update billing to regain access.';
        }

        if (isTrial(activeCompany)) {
          if (trialDaysRemaining !== null) {
            const label = trialDaysRemaining === 1 ? 'day' : 'days';
            return `${String(trialDaysRemaining)} ${label} remaining in trial.`;
          }
          return 'Trial active. Billing details are required to avoid interruption.';
        }

        return 'Subscription active.';
      })()
    : null;

  const billingBanner =
    activeCompany && bannerMessage ? (
      <Stack
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          px: { xs: 2, sm: 2.5, lg: 3 },
          py: 1.5,
        }}
      >
        <Alert
          severity={planUsable ? (isTrial(activeCompany) ? 'info' : 'success') : 'warning'}
          action={
            <Button
              component={NavLink}
              to="/billing"
              color="inherit"
              size="small"
              variant="outlined"
            >
              Billing
            </Button>
          }
        >
          <Stack spacing={0.25}>
            <Typography variant="body2" fontWeight={700}>
              {formatPlanBillingLabel(activeCompany)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {bannerMessage}
            </Typography>
          </Stack>
        </Alert>
      </Stack>
    ) : null;

  return (
    <AppShell
      workspaceLabel="Company workspace"
      homePath="/"
      navGroups={visibleNavGroups}
      workspaceSwitcher={<WorkspaceSwitcher />}
      accountEmail={currentUser?.email ?? bootstrap?.user.email ?? null}
      companyLinkLabel={activeCompanyName ? `Company: ${activeCompanyName}` : 'Select company'}
      billingBanner={billingBanner}
      onLogout={() => {
        auth.logout();
      }}
    />
  );
}
